import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin, useStore } from '../store/store';
import { reloadRecord, saveRecord } from './actions';
import { ConfirmProgressMove } from './context';
import { useData } from './dataStore';
import { EditableFields } from './FieldEdit';
import { DialoguePreview, isDialogue } from './DialoguePreview';
import { classChain } from './schema';
import { same } from './values';
import type { DataRecord, Values } from './types';

/**
 * The editable Properties tab. The draft is saved against the rev it started from, so someone else's
 * save in between is never silently overwritten (409 → "load theirs" / "put mine on top").
 */
export function RecordEditor({ record }: { record: DataRecord }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const cat = useData((s) => s.categories.find((c) => c.key === record.category));
  const classes = useData((s) => s.classes);
  const [previewing, setPreviewing] = useState(false);
  const [base, setBase] = useState(record);
  const [draft, setDraft] = useState<Values>(record.values ?? {});
  const [note, setNote] = useState(record.needsNiklas);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const dirty = !same(draft, base.values) || note !== base.needsNiklas;

  // Someone else saved (realtime) while this form had no unsaved changes: show their version.
  if (record.rev !== base.rev && !dirty && !saving) {
    setBase(record);
    setDraft(record.values ?? {});
    setNote(record.needsNiklas);
  }

  const keyField = cat?.keyField;
  const locked: Record<string, string> = {};
  if (keyField && record.guid && !isAdmin(user)) locked[keyField] = t('data.edit.keyLocked');

  // Saves keep quest progress by step and objective position: in a quest that is in Unity, moving or
  // deleting them asks once.
  const progressConfirmed = useRef(false);
  const confirmProgressMove = () => {
    if (!record.guid || progressConfirmed.current) return true;
    progressConfirmed.current = confirm(t('data.edit.confirmProgressMove'));
    return progressConfirmed.current;
  };

  const save = async () => {
    setSaving(true);
    const res = await saveRecord(record.id, base.rev, { values: draft, needsNiklas: note });
    setSaving(false);
    if (res.ok) {
      setBase(res.record);
      setDraft(res.record.values ?? {});
      setNote(res.record.needsNiklas);
    } else if (res.conflict) setConflict(true);
  };

  const loadTheirs = async () => {
    const latest = await reloadRecord(record.id);
    if (!latest) return;
    setBase(latest);
    setDraft(latest.values ?? {});
    setNote(latest.needsNiklas);
    setConflict(false);
  };

  /** Re-applies only the fields this user changed onto the latest version. */
  const mineOnTop = async () => {
    const latest = await reloadRecord(record.id);
    if (!latest) return;
    const merged: Values = { ...(latest.values ?? {}) };
    for (const k of Object.keys(draft)) if (!same(draft[k], base.values?.[k])) merged[k] = draft[k];
    setBase(latest);
    setDraft(merged);
    if (note === base.needsNiklas) setNote(latest.needsNiklas);
    setConflict(false);
  };

  return (
    <div className="record-editor">
      {conflict && (
        <div className="banner warn">
          <span>{t('errors.stale_revision')}</span>
          <div className="row">
            <button onClick={() => void loadTheirs()}>{t('data.edit.loadTheirs')}</button>
            <button className="primary" onClick={() => void mineOnTop()}>
              {t('data.edit.mineOnTop')}
            </button>
          </div>
        </div>
      )}
      <label className="note-to-admin">
        {t('data.edit.note')}
        <textarea
          rows={2}
          value={note}
          placeholder={t('data.edit.notePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {isDialogue(classChain(classes, record.className)) && (
        <div>
          <button className="small" onClick={() => setPreviewing(true)}>
            ▶ {t('data.dialogue.preview')}
          </button>
        </div>
      )}
      {previewing && <DialoguePreview values={draft} onClose={() => setPreviewing(false)} />}
      <ConfirmProgressMove.Provider value={confirmProgressMove}>
        <EditableFields
          className={record.className}
          values={draft}
          unityValues={record.guid ? record.unityValues : null}
          locked={locked}
          onChange={setDraft}
        />
      </ConfirmProgressMove.Provider>
      {dirty && (
        <div className="save-bar">
          <span className="muted small">{t('data.edit.unsaved')}</span>
          <div className="spacer" />
          <button
            onClick={() => {
              setDraft(base.values ?? {});
              setNote(base.needsNiklas);
            }}
          >
            {t('common.reset')}
          </button>
          <button className="primary" disabled={saving || conflict} onClick={() => void save()}>
            {t('common.save')}
          </button>
        </div>
      )}
    </div>
  );
}
